import { getCharacteristicIds } from 'Models/Entities/AlterationEntity';
import { store } from 'Models/Store';
import { hasOverlap } from 'Util/_HumanWritten/ArrayUtils';
import { CheckInBookingOverviewDto } from '../CheckInEntities/CheckInBookingOverviewDto';
import { CapacityTypeForCheckInDto } from '../Types/CapacityTypeForCheckInDto';
import { CargoTypeForCheckInDto } from '../Types/CargoTypeForCheckInDto';

// ---------------------------------------------------------------------------
// Cargo characteristic matching — shared by the loading-summary capacity rows and the vehicle
// load breakdown table. Resolves a booking's vehicle/trailer characteristic sets (mirroring the
// server capacity calculation) and assigns each side to a single capacity type.
// ---------------------------------------------------------------------------

export interface BookingCargoCharacteristics {
	hasVehicle: boolean;
	hasTrailer: boolean;
	vehicleCharacteristics: string[];
	trailerCharacteristics: string[];
}

/**
 * Resolves the vehicle and trailer characteristic id sets for a booking, mirroring the server
 * capacity calculation: the vehicle and trailer are each matched independently (so a booking can
 * contribute for both), and characteristics flagged "applies to linked trailer/vehicle" present on
 * either side are cross-applied to both when the booking has both.
 */
export function getBookingCargoCharacteristics(
	booking: CheckInBookingOverviewDto,
	vehicleTypeMap: Map<string, CargoTypeForCheckInDto>,
	linkedApplicableIds: Set<string>,
): BookingCargoCharacteristics {
	const summary = booking.bookedSummary;
	const cargoInfo = summary?.cargoInfo;
	const towOnInfo = summary?.towOnInfo;
	const alterationContext = summary?.alterationContext;

	const hasVehicle = !!cargoInfo;
	const hasTrailer = !!towOnInfo;

	// Vehicle: customer-selected characteristics combined with the cargo type's own.
	let vehicleCharacteristics: string[] = [];
	if (hasVehicle) {
		const vehicleType = vehicleTypeMap.get(cargoInfo!.cargoTypeId);
		vehicleCharacteristics = [
			...getCharacteristicIds(alterationContext?.cargoCharacteristics ?? []),
			...(vehicleType?.characteristicIds ?? []),
		];
	}

	// Trailer: customer-selected trailer characteristics combined with the tow-on type's own.
	let trailerCharacteristics: string[] = [];
	if (hasTrailer) {
		const towOnTypeCharacteristics = towOnInfo!.towOnTypeId
			? (store.towOnTypeCharacteristicsMap[towOnInfo!.towOnTypeId] ?? [])
			: [];
		trailerCharacteristics = [
			...getCharacteristicIds(alterationContext?.trailerCargoCharacteristics ?? []),
			...towOnTypeCharacteristics,
		];
	}

	// Cross-apply "applies to linked trailer/vehicle" characteristics: any flagged characteristic on
	// either side applies to both when the booking has a vehicle and a trailer.
	if (hasVehicle && hasTrailer && linkedApplicableIds.size > 0) {
		const shared = [...vehicleCharacteristics, ...trailerCharacteristics].filter(id => linkedApplicableIds.has(id));
		if (shared.length > 0) {
			vehicleCharacteristics = [...vehicleCharacteristics, ...shared];
			trailerCharacteristics = [...trailerCharacteristics, ...shared];
		}
	}

	return { hasVehicle, hasTrailer, vehicleCharacteristics, trailerCharacteristics };
}

export function getLinkedApplicableIds(): Set<string> {
	return new Set(store.cargoCharacteristics.filter(c => c.appliesToLinkedTrailerVehicle).map(c => c.id));
}

/**
 * Id of the last capacity type (in configured order) whose characteristics overlap the given side's
 * characteristics, or null when it matches none. A vehicle/trailer that matches several capacity types
 * (e.g. an electric truck) is assigned to only the last, so each is counted exactly once across the
 * nested rows and the numbers stay a true representation of the vehicles and trailers on board.
 */
export function lastMatchingCapacityTypeId(
	sideCharacteristics: string[],
	capacityTypes: CapacityTypeForCheckInDto[],
): string | null {
	for (let i = capacityTypes.length - 1; i >= 0; i--) {
		if (hasOverlap(sideCharacteristics, capacityTypes[i].cargoCharacteristicIds)) {
			return capacityTypes[i].id;
		}
	}
	return null;
}

/**
 * Whether a capacity type may be named in staff-facing views (the vehicle load table and the tab it
 * filters to). A capacity type whose linked characteristics are every one of them hidden from staff
 * exists only to shape capacity behind the scenes, so naming it would leak what the characteristic's
 * visibility flags say to keep hidden.
 *
 * Hidden types are dropped from the list the load breakdown groups by, not from the built rows — the
 * vehicles that matched them fall through to their next matching visible type, or to Cars, so every
 * vehicle is still counted exactly once and the totals are unchanged.
 *
 * A type with no linked characteristics has nothing to hide and stays visible.
 */
export function isCapacityTypeVisibleToStaff(capacityType: CapacityTypeForCheckInDto): boolean {
	const characteristicIds = capacityType.cargoCharacteristicIds;
	if (characteristicIds.length === 0) {
		return true;
	}
	return characteristicIds.some(id => store.cargoCharacteristicMap[id]?.showToStaff !== false);
}
